import { useCallback, useEffect, useMemo, useState } from "react";
import {
  assignServiceRouteEvent,
  createServiceRoute,
  listServiceMapSuggestions,
  listServiceRoutes,
  reorderServiceRouteEvents,
} from "@/lib/service/client";
import type { ServiceMapSuggestion, ServiceRig, ServiceTech } from "@/lib/service/types";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function useServiceMapPanel(
  navTick: number,
  rigs: ServiceRig[],
  techs: ServiceTech[]
) {
  const [routeDate, setRouteDate] = useState(todayIso);
  const [selectedRigId, setSelectedRigId] = useState("");
  const [selectedTechId, setSelectedTechId] = useState("");
  const [suggestions, setSuggestions] = useState<ServiceMapSuggestion[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [totalDriveHint, setTotalDriveHint] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [listBusy, setListBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [applyMessage, setApplyMessage] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    setApplyMessage(null);
    const res = await listServiceMapSuggestions({
      route_date: routeDate,
      rig_id: selectedRigId || undefined,
      tech_id: selectedTechId || undefined,
      horizon_days: 7,
    });
    setListBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      setSuggestions([]);
      setWarnings([]);
      setMapCenter(null);
      return;
    }
    setSuggestions(res.data.suggestions ?? []);
    setWarnings(res.data.warnings ?? []);
    setTotalDriveHint(res.data.total_drive_hint_miles ?? null);
    setMapCenter(res.data.map_center ?? null);
  }, [routeDate, selectedRigId, selectedTechId]);

  useEffect(() => {
    void reload();
  }, [reload, navTick]);

  const orderedEventIds = useMemo(
    () =>
      [...suggestions]
        .sort((a, b) => a.sequence - b.sequence)
        .map((s) => s.event_id),
    [suggestions]
  );

  const applyToRoute = useCallback(async () => {
    if (!selectedRigId) {
      setLocalError("Pick a rig before applying the suggested route.");
      return;
    }
    if (orderedEventIds.length === 0) {
      setLocalError("No suggested stops to apply.");
      return;
    }

    setApplyBusy(true);
    setLocalError(null);
    setApplyMessage(null);

    const routesRes = await listServiceRoutes({
      route_date: routeDate,
      include_events: true,
    });
    if (!routesRes.ok) {
      setApplyBusy(false);
      setLocalError(routesRes.error);
      return;
    }

    const routes = routesRes.data.routes as Array<{
      id: string;
      rig_id: string | null;
      tech_id: string | null;
      stops?: Array<{ event: { id: string } }>;
    }>;

    let routeId = routes.find((r) => r.rig_id === selectedRigId)?.id;

    if (!routeId) {
      const created = await createServiceRoute({
        route_date: routeDate,
        rig_id: selectedRigId,
        tech_id: selectedTechId || null,
        label: `Map suggestion ${routeDate}`,
      });
      if (!created.ok) {
        setApplyBusy(false);
        setLocalError(created.error);
        return;
      }
      routeId = created.data.route.id;
    }

    const existingRoute = routes.find((r) => r.id === routeId);
    const onRoute = new Set(
      (existingRoute?.stops ?? []).map((s) => s.event.id)
    );

    const assignWarnings: string[] = [];
    for (const eventId of orderedEventIds) {
      if (onRoute.has(eventId)) continue;
      const assigned = await assignServiceRouteEvent({ route_id: routeId!, event_id: eventId });
      if (!assigned.ok) {
        setApplyBusy(false);
        setLocalError(assigned.error);
        return;
      }
      if (assigned.data.warnings?.length) assignWarnings.push(...assigned.data.warnings);
      onRoute.add(eventId);
    }

    const refreshed = await listServiceRoutes({
      route_date: routeDate,
      include_events: true,
    });
    if (!refreshed.ok) {
      setApplyBusy(false);
      setLocalError(refreshed.error);
      return;
    }

    const routeDetail = (refreshed.data.routes as Array<{
      id: string;
      stops?: Array<{ route_event: { sequence: number }; event: { id: string } }>;
    }>).find((r) => r.id === routeId);

    const existingStops = [...(routeDetail?.stops ?? [])].sort(
      (a, b) => a.route_event.sequence - b.route_event.sequence
    );
    const existingIds = existingStops.map((s) => s.event.id);

    if (existingIds.length === 0) {
      setApplyBusy(false);
      setApplyMessage("No stops on route after apply.");
      return;
    }

    const suggestedSet = new Set(orderedEventIds);
    const finalOrder = [
      ...orderedEventIds.filter((id) => existingIds.includes(id)),
      ...existingIds.filter((id) => !suggestedSet.has(id)),
    ];

    const reorderRes = await reorderServiceRouteEvents({
      route_id: routeId!,
      ordered_event_ids: finalOrder,
    });
    setApplyBusy(false);
    if (!reorderRes.ok) {
      setLocalError(reorderRes.error);
      return;
    }

    const warnNote =
      assignWarnings.length > 0
        ? ` (${assignWarnings.length} rig warning(s) — review on the Board.)`
        : "";
    setApplyMessage(
      `Applied ${orderedEventIds.length} suggested stop(s) to route (${finalOrder.length} total). Dispatcher confirmation required before dispatch.${warnNote}`
    );
  }, [orderedEventIds, routeDate, selectedRigId, selectedTechId]);

  const onRigChange = useCallback(
    (rigId: string) => {
      setSelectedRigId(rigId);
      const rig = rigs.find((r) => r.id === rigId);
      if (rig?.standard_tech_id) setSelectedTechId(rig.standard_tech_id);
    },
    [rigs]
  );

  return {
    routeDate,
    setRouteDate,
    selectedRigId,
    setSelectedRigId: onRigChange,
    selectedTechId,
    setSelectedTechId,
    suggestions,
    warnings,
    totalDriveHint,
    mapCenter,
    listBusy,
    applyBusy,
    localError,
    applyMessage,
    reload,
    applyToRoute,
    rigs,
    techs,
    orderedEventIds,
  };
}
